import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Shield, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";

// DPI page — Dispositivi di Protezione Individuale per uso fitosanitari

const DPI_ICONS = ["😷", "🛡️", "🥼", "🧤", "👁️", "🥾"];

export default function DPI() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items = t("dpi.items", { returnObjects: true });
  const dressingSteps = t("dpi.dressingSteps", { returnObjects: true });
  const undressingSteps = t("dpi.undressingSteps", { returnObjects: true });
  const expiryItems = t("dpi.expiryItems", { returnObjects: true });

  return (
    <div className="min-h-screen pb-24" data-testid="dpi-page">
      <Header
        title={t("dpi.headerTitle")}
        subtitle={t("dpi.headerSubtitle")}
        right={
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg"
            style={{ border: "2px solid var(--border)" }}
            aria-label={t("dpi.backAriaLabel")} data-testid="dpi-back">
            <ArrowLeft size={18} />
          </button>
        }
      />

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">

        {/* AVVISO IN ALTO — grassetto, sfondo oro, ben visibile */}
        <section className="vs-card p-5" style={{
          background: "var(--gold)",
          border: "2px solid #7a4b00",
          borderLeft: "6px solid var(--accent)",
          color: "#3D2300",
        }}>
          <div className="flex items-start gap-3">
            <AlertTriangle size={28} strokeWidth={2.6} style={{ color: "var(--accent)", flexShrink: 0 }} />
            <p className="text-sm font-extrabold leading-relaxed" style={{ color: "#3D2300" }}>
              {t("dpi.topBanner")}
            </p>
          </div>
        </section>

        {/* Intro normativa */}
        <section className="vs-card p-5" style={{ borderLeft: "4px solid var(--accent)" }}>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: "var(--gradient-vine)" }}>
              <Shield size={22} color="white" strokeWidth={2.4} />
            </div>
            <div>
              <h2 className="heading text-xl font-extrabold mb-1">{t("dpi.obligationTitle")}</h2>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                <strong>{t("dpi.obligationTextBold")}</strong> {t("dpi.obligationTextSuffix")}
              </p>
            </div>
          </div>
        </section>

        {/* I 6 DPI */}
        <h2 className="heading text-xl font-extrabold mt-2">{t("dpi.itemsTitle")}</h2>

        {items.map((dpi, idx) => (
          <article key={idx} className="vs-card p-4" data-testid={`dpi-item-${idx}`}>
            <div className="flex items-center gap-3 mb-3">
              <div style={{ fontSize: 32 }}>{DPI_ICONS[idx]}</div>
              <h3 className="heading text-lg font-extrabold">{dpi.title}</h3>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-3">
              {dpi.sigle.map((s) => (
                <code key={s}
                  className="text-xs px-2 py-1 rounded font-mono font-bold"
                  style={{
                    background: "var(--secondary)",
                    border: "1.5px solid var(--border)",
                    color: "var(--primary)",
                  }}>
                  {s}
                </code>
              ))}
            </div>

            <div className="mb-3">
              <div className="text-xs font-extrabold uppercase tracking-wider mb-1.5"
                style={{ color: "var(--primary)" }}>
                {t("dpi.goodLabel")}
              </div>
              <ul className="space-y-1">
                {dpi.good.map((g, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: "var(--primary)" }} />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="text-xs font-extrabold uppercase tracking-wider mb-1.5"
                style={{ color: "var(--accent)" }}>
                {t("dpi.badLabel")}
              </div>
              <ul className="space-y-1">
                {dpi.bad.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <XCircle size={16} className="shrink-0 mt-0.5" style={{ color: "var(--accent)" }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}

        {/* Vestizione */}
        <section className="vs-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <span style={{ fontSize: 24 }}>👔</span>
            <h2 className="heading text-lg font-extrabold">{t("dpi.dressingTitle")}</h2>
          </div>
          <ol className="space-y-2">
            {dressingSteps.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="shrink-0 w-7 h-7 rounded-full font-extrabold flex items-center justify-center text-xs"
                  style={{ background: "var(--primary)", color: "white" }}>{i + 1}</span>
                <span style={{ paddingTop: 3 }}>{s}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* Svestizione */}
        <section className="vs-card p-5" style={{ borderLeft: "4px solid var(--gold)" }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={22} style={{ color: "#7a4b00" }} />
            <h2 className="heading text-lg font-extrabold">{t("dpi.undressingTitle")}</h2>
          </div>
          <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
            {t("dpi.undressingWarning")}
          </p>
          <ol className="space-y-2">
            {undressingSteps.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="shrink-0 w-7 h-7 rounded-full font-extrabold flex items-center justify-center text-xs"
                  style={{ background: "var(--gold)", color: "#5a3500" }}>{i + 1}</span>
                <span style={{ paddingTop: 3 }}>{s}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* Scadenze */}
        <section className="vs-card p-5">
          <h2 className="heading text-lg font-extrabold mb-3">{t("dpi.expiryTitle")}</h2>
          <div className="space-y-2 text-sm">
            {expiryItems.map((item, i) => (
              <div key={i}><strong>{item.label}</strong> {item.value}</div>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <div className="p-3 rounded-xl text-xs"
          style={{ background: "var(--secondary)", border: "1.5px solid var(--border)", color: "var(--text-secondary)" }}>
          ℹ️ {t("dpi.disclaimer")}
        </div>
      </main>

      <BottomNav />
    </div>
  );
}
